import { type DocumentMeta } from "@prismicio/editor-fields"
import { request } from "@prismicio/editor-support/Http"
import { z } from "zod"

import { GenerateLandingPagesState } from "./GenerateLandingPagesState"
import { ImportCompaniesState } from "./ImportCompaniesState"
import type { LandingPagesApiClient } from "./LandingPagesApiClient"

interface ABMLandingPagesApiClientOptions {
  baseUrl: URL
  repository: string
  credentials: RequestCredentials | undefined
}

interface CreateMarketingCampaignBody {
  csv: string
  releaseName: string | null
  releaseId: string | null
  baselineDocumentId: string
  repository: string
}

export class ABMLandingPagesApiClient implements LandingPagesApiClient {
  public repository: string
  private baseUrl: URL
  private credentials: RequestCredentials | undefined

  constructor({
    baseUrl,
    repository,
    credentials,
  }: ABMLandingPagesApiClientOptions) {
    this.baseUrl = baseUrl
    this.repository = repository
    this.credentials = credentials
  }

  async startGenerateLandingPages(args: {
    document: DocumentMeta
    csv: string
  }): Promise<string> {
    const { document, csv } = args
    const url = new URL("marketing-campaign", this.baseUrl)
    url.searchParams.set("repository", this.repository)

    const response = await request(url, {
      method: "POST",
      body: {
        csv,
        releaseName: document.title,
        releaseId: null,
        baselineDocumentId: document.id,
        repository: this.repository,
      } satisfies CreateMarketingCampaignBody,
      credentials: this.credentials,
      okType: z.object({
        executionArn: z.string(),
      }),
    })

    return response.executionArn
  }

  async checkGenerateLandingPagesStatus(query: {
    executionArn: string
  }): Promise<GenerateLandingPagesState> {
    const url = new URL("status", `${this.baseUrl.href}/marketing-campaign/`)
    url.searchParams.set("executionArn", query.executionArn)

    const response = await request(url, {
      method: "GET",
      credentials: this.credentials,
      okType: GenerateLandingPagesState,
    })

    return response
  }

  async startImportCompanies(args: { csv: string }): Promise<string> {
    const { csv } = args
    const url = new URL("companies", this.baseUrl)
    url.searchParams.set("repository", this.repository)

    const response = await request(url, {
      method: "POST",
      body: {
        csv,
      },
      credentials: this.credentials,
      okType: z.object({
        executionArn: z.string(),
      }),
    })

    return response.executionArn
  }

  async checkImportCompaniesStatus(query: {
    executionArn: string
  }): Promise<ImportCompaniesState> {
    const url = new URL("status", `${this.baseUrl.href}companies/`)
    url.searchParams.set("executionArn", query.executionArn)

    const response = await request(url, {
      method: "GET",
      credentials: this.credentials,
      okType: ImportCompaniesState,
    })

    return response
  }

  async getFeedbackRunId(versionId: string): Promise<string | null> {
    const url = new URL("feedback", this.baseUrl.href)
    url.searchParams.set("versionId", versionId)
    url.searchParams.set("repository", this.repository)

    const response = await request(url, {
      method: "GET",
      credentials: this.credentials,
      okType: z.object({
        runId: z.string().nullable(),
      }),
    })

    return response.runId
  }

  async createFeedback(args: {
    runId: string
    versionId: string
    score: number
  }): Promise<void> {
    const { runId, versionId, score } = args
    const url = new URL("feedback", this.baseUrl.href)
    url.searchParams.set("runId", args.runId)
    url.searchParams.set("repository", this.repository)

    await request(url, {
      method: "POST",
      credentials: this.credentials,
      body: {
        score,
        runId,
        versionId,
      },
      okType: z.any(),
    })
  }
}
